/**
 * 服务器上挂着的安卓安装包（CR-058）。
 *
 * 一键部署打完 debug APK 后，把它和一份 `latest.json` 放到服务器的 `/downloads/` 下（Caddy 直接给静态文件）。
 * 登录页读这份清单：读得到才显示「下载安卓 App」，读不到（本地开发、还没传过 APK）就什么都不显示。
 */

export interface AppRelease {
  /** 安装包文件名，在 /downloads/ 下 */
  file: string;
  /** 给人看的版本号，0.0.1 起每次部署 +1（CR-068）。CR-068 之前的清单这里是 git 短 SHA */
  version: string;
  /** 构建自哪个 commit（git 短 SHA），排查「手机上装的是哪一版」看它。老清单没有 */
  commit?: string;
  /**
   * 安卓的 versionCode（CR-067）。App 内检查更新比的就是它 ——
   * 版本名是 git SHA，没有大小之分；覆盖安装也要求它只增不减。
   */
  versionCode: number;
  /**
   * 这个包是正式签名包还是测试包（CR-067）。登录页据此决定要不要标「测试版」——
   * 以前这两个字写死在文案里，换成正式包之后还挂着，看的人以为发错了包。
   * 字段缺失（万一是别的地方生成的清单）按正式包处理：宁可不标，也不能给签名包乱扣帽子。
   */
  buildType: 'debug' | 'release';
  /** ISO 时间 */
  builtAt: string;
  sizeBytes: number;
  /**
   * 低于这个 versionCode 就必须更新才能用（CR-067 预留，现在部署不写这个字段）。
   * 真要用的时候，服务器上的 latest.json 填一个数就生效，不用发新包。
   */
  minVersionCode?: number;
}

export const APP_RELEASE_URL = '/downloads/latest.json';

const positiveInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

/** 清单是服务器上的文件，不是接口 —— 字段缺了、文件名不像 APK，都当没有 */
export function parseAppRelease(value: unknown): AppRelease | null {
  if (!value || typeof value !== 'object') return null;
  const { file, version, commit, versionCode, buildType, builtAt, sizeBytes, minVersionCode } =
    value as Record<string, unknown>;
  if (typeof file !== 'string' || !/^[\w.-]+\.apk$/.test(file)) return null;
  if (typeof version !== 'string' || typeof builtAt !== 'string') return null;
  if (Number.isNaN(Date.parse(builtAt))) return null;
  if (!positiveInt(sizeBytes)) return null;
  // CR-067 之前的清单没有 versionCode：当作「读不出版本」，登录页照常显示下载，
  // App 里就不提示更新 —— 宁可不提示，也不能拿旧清单误判成有新版
  if (!positiveInt(versionCode)) return null;
  return {
    file,
    version,
    versionCode,
    buildType: buildType === 'debug' ? 'debug' : 'release',
    builtAt,
    sizeBytes,
    ...(typeof commit === 'string' && commit ? { commit } : {}),
    ...(positiveInt(minVersionCode) ? { minVersionCode } : {}),
  };
}

/**
 * @param url 默认取当前站点下的清单（登录页就在服务器上）。
 *   **App 里必须把服务器地址传进来**（CR-067）：App 的页面来源是 `http://localhost`，
 *   相对地址会打到 App 自己的包里，永远读不到清单。
 */
export async function fetchAppRelease(url: string = APP_RELEASE_URL): Promise<AppRelease | null> {
  try {
    const response = await fetch(url, { cache: 'no-store' });
    // 本地开发时 Vite 对不存在的路径回 index.html（200、text/html），所以还要看类型
    if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) return null;
    return parseAppRelease(await response.json());
  } catch {
    return null;
  }
}
